import type { BrandManagedCollabLogistics, BrandManagedLogisticsType, Campaign, Collab } from '@foodfluence/shared';

export const BRAND_MANAGED_LOGISTICS_CONFIG: Record<
  BrandManagedLogisticsType,
  { label: string; description: string }
> = {
  ship: {
    label: 'Product will be shipped to you',
    description: 'Share your shipping address after the collab is confirmed.',
  },
  retailPickup: {
    label: 'Pick up in retail',
    description: "You'll be directed to a specific store where your item will be covered at checkout.",
  },
  creatorVisit: {
    label: 'Visit a location',
    description: 'A time and place will be coordinated for you to visit.',
  },
  serviceDate: {
    label: 'Scheduled service',
    description: 'A specific appointment time will be booked for you to receive the service.',
  },
  digital: {
    label: 'Digital product',
    description: "You'll receive access (login, download link, etc.) via chat.",
  },
  onlinePurchase: {
    label: 'Purchase online + get reimbursed',
    description: "Purchase from their online store and you'll be reimbursed after.",
  },
  other: {
    label: 'Logistics',
    description: 'The brand will share details in your Inbox.',
  },
};

export function getBrandManagedLogisticsLabel(logistics: BrandManagedCollabLogistics): string {
  return BRAND_MANAGED_LOGISTICS_CONFIG[logistics.type]?.label ?? 'Logistics';
}

export function getBrandManagedLogisticsDescription(logistics: BrandManagedCollabLogistics): string {
  const config = BRAND_MANAGED_LOGISTICS_CONFIG[logistics.type];
  return config?.description ?? '';
}

/** Brand-provided logistics notes shown separately from the default type description. */
export function getBrandManagedLogisticsBrandInstructions(
  logistics: BrandManagedCollabLogistics
): string {
  return logistics.customInstructions?.trim() ?? '';
}

export function isBrandManagedProductCompensation(campaign?: Campaign, collab?: Collab): boolean {
  if (campaign?.campaignType !== 'brandManaged') return false;
  const productValue = campaign.productValue ?? 0;
  const credit = collab?.credit ?? campaign.credit ?? 0;
  return productValue > 0 && credit <= 0;
}

export function isBrandManagedCreditCompensation(campaign?: Campaign, collab?: Collab): boolean {
  if (campaign?.campaignType !== 'brandManaged') return false;
  if (isBrandManagedProductCompensation(campaign, collab)) return false;
  const credit = collab?.credit ?? campaign.credit ?? 0;
  return credit > 0;
}

export function hasCampaignCompensationOffer(campaign?: Campaign, collab?: Collab): boolean {
  const cash = collab?.cash ?? campaign?.cash ?? 0;
  const credit = collab?.credit ?? campaign?.credit ?? 0;
  const productValue = campaign?.productValue ?? 0;
  return cash > 0 || credit > 0 || productValue > 0;
}

export type BrandManagedCompensationFaqKind = 'product' | 'credit';

/** Shared inbox-first FAQ for Direct Coordination product or credit compensation. */
export function getBrandManagedCompensationFaq(kind: BrandManagedCompensationFaqKind): {
  title: string;
  steps: string[];
} {
  const inboxStep = 'Accept the collaboration and open your Inbox.';
  const submitStep = 'Create and submit your content through your collab dashboard.';

  if (kind === 'product') {
    return {
      title: 'How do I receive my product?',
      steps: [
        inboxStep,
        'Message the brand to confirm delivery details and follow the logistics instructions in the brief.',
        submitStep,
      ],
    };
  }

  return {
    title: 'How do I receive my store credit?',
    steps: [
      inboxStep,
      'The brand will share how your store credit will be provided—follow their instructions in the brief.',
      submitStep,
    ],
  };
}
